import { ApiResponse } from '@shared/types';
import { PaginatedResponse } from '../types';
import {
  InventoryCategoryRecord,
  InventoryItemFilterParams,
  InventoryItemRecord,
  InventoryItemSummaryCounts,
  InventoryUnitRecord,
  SaveInventoryCategoryRequest,
  SaveInventoryItemRequest,
  SaveInventoryUnitRequest,
} from '../types/inventory';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;

const toNumber = (value: unknown) => {
  if (typeof value === 'number') {
    return value;
  }

  return Number(value || 0);
};

const mapInventoryItem = (
  value: Record<string, unknown>,
): InventoryItemRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  sku: (value.sku as string | null) ?? null,
  name: String(value.name || ''),
  description: (value.description as string | null) ?? null,
  category_id: (value.category_id as string | null) ?? null,
  purchase_unit_id: String(value.purchase_unit_id || ''),
  stock_unit_id: String(value.stock_unit_id || ''),
  conversion_rate: toNumber(value.conversion_rate),
  costing_method: value.costing_method as InventoryItemRecord['costing_method'],
  reorder_level: toNumber(value.reorder_level),
  item_type: (value.item_type as InventoryItemRecord['item_type']) ?? 'resale',
  is_active: Boolean(value.is_active),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const mapInventoryCategory = (
  value: Record<string, unknown>,
): InventoryCategoryRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  name: String(value.name || ''),
  description: (value.description as string | null) ?? null,
  is_active: Boolean(value.is_active),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const mapInventoryUnit = (
  value: Record<string, unknown>,
): InventoryUnitRecord => ({
  id: String(value.id || ''),
  company_id: toNumber(value.company_id),
  name: String(value.name || ''),
  abbreviation: (value.abbreviation as string | null) ?? null,
  is_active: Boolean(value.is_active),
  created_by: (value.created_by as string | null) ?? null,
  created_at: String(value.created_at || ''),
  updated_at: String(value.updated_at || ''),
});

const applyInventoryItemFilters = <T>(
  query: T,
  filters: {
    search?: string;
    categoryId?: InventoryItemFilterParams['categoryId'];
    status?: InventoryItemFilterParams['status'];
    itemIds?: InventoryItemFilterParams['itemIds'];
  },
) => {
  let nextQuery = query as any;
  const trimmedSearch = filters.search?.trim();

  if (filters.categoryId && filters.categoryId !== 'all') {
    nextQuery = nextQuery.eq('category_id', filters.categoryId);
  }

  if (filters.status && filters.status !== 'all') {
    nextQuery = nextQuery.eq('is_active', filters.status === 'active');
  }

  if (filters.itemIds?.length) {
    nextQuery = nextQuery.in('id', filters.itemIds);
  }

  if (trimmedSearch) {
    nextQuery = nextQuery.or(
      `sku.ilike.%${trimmedSearch}%,name.ilike.%${trimmedSearch}%,description.ilike.%${trimmedSearch}%`,
    );
  }

  return nextQuery as T;
};

export const fetchInventoryItems = async ({
  companyId,
  search,
  categoryId = 'all',
  status = 'all',
  itemIds = [],
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: InventoryItemFilterParams): Promise<
  ApiResponse<PaginatedResponse<InventoryItemRecord>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('inventory_items')
      .select('*', { count: 'exact' })
      .eq('company_id', companyId)
      .order('name', { ascending: true })
      .range(from, to);

    query = applyInventoryItemFilters(query, {
      search,
      categoryId,
      status,
      itemIds,
    });

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    return {
      success: true,
      data: {
        data: (data || []).map((item) =>
          mapInventoryItem(item as Record<string, unknown>),
        ),
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory items',
      data: undefined,
    };
  }
};

export const fetchInventoryItemSummary = async (
  companyId: number,
  search?: string,
  categoryId: InventoryItemFilterParams['categoryId'] = 'all',
  status: InventoryItemFilterParams['status'] = 'all',
): Promise<ApiResponse<InventoryItemSummaryCounts>> => {
  try {
    const baseQuery = (activeStatus?: boolean) => {
      let query = supabase
        .from('inventory_items')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId);

      query = applyInventoryItemFilters(query, {
        search,
        categoryId,
        status:
          activeStatus === undefined
            ? status
            : activeStatus
              ? 'active'
              : 'inactive',
      });

      return query;
    };

    const [totalResult, activeResult, inactiveResult, categoriesResult] =
      await Promise.all([
        baseQuery(),
        baseQuery(true),
        baseQuery(false),
        supabase
          .from('inventory_categories')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('is_active', true),
      ]);

    const errors = [
      totalResult.error,
      activeResult.error,
      inactiveResult.error,
      categoriesResult.error,
    ].filter(Boolean);

    if (errors.length) {
      return { success: false, error: errors[0]?.message, data: undefined };
    }

    return {
      success: true,
      data: {
        totalItems: totalResult.count || 0,
        activeItems: activeResult.count || 0,
        inactiveItems: inactiveResult.count || 0,
        categoryCount: categoriesResult.count || 0,
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory summary',
      data: undefined,
    };
  }
};

export const fetchInventoryCategories = async (
  companyId: number,
): Promise<ApiResponse<InventoryCategoryRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('inventory_categories')
      .select('*')
      .eq('company_id', companyId)
      .order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) =>
        mapInventoryCategory(item as Record<string, unknown>),
      ),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory categories',
      data: [],
    };
  }
};

export const fetchInventoryUnits = async (
  companyId: number,
): Promise<ApiResponse<InventoryUnitRecord[]>> => {
  try {
    const { data, error } = await supabase
      .from('inventory_units')
      .select('*')
      .eq('company_id', companyId)
      .order('name', { ascending: true });

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((item) =>
        mapInventoryUnit(item as Record<string, unknown>),
      ),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch inventory units',
      data: [],
    };
  }
};

export const saveInventoryCategory = async (
  input: SaveInventoryCategoryRequest,
): Promise<ApiResponse<InventoryCategoryRecord>> => {
  const payload = {
    company_id: input.companyId,
    name: input.name.trim(),
    description: input.description?.trim() || null,
    is_active: input.isActive ?? true,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('inventory_categories')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapInventoryCategory(data) };
    }

    const { data, error } = await supabase
      .from('inventory_categories')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapInventoryCategory(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save inventory category',
      data: undefined,
    };
  }
};

export const saveInventoryUnit = async (
  input: SaveInventoryUnitRequest,
): Promise<ApiResponse<InventoryUnitRecord>> => {
  const payload = {
    company_id: input.companyId,
    name: input.name.trim(),
    abbreviation: input.abbreviation?.trim() || null,
    is_active: input.isActive ?? true,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('inventory_units')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapInventoryUnit(data) };
    }

    const { data, error } = await supabase
      .from('inventory_units')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapInventoryUnit(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save inventory unit',
      data: undefined,
    };
  }
};

export const saveInventoryItem = async (
  input: SaveInventoryItemRequest,
): Promise<ApiResponse<InventoryItemRecord>> => {
  const payload = {
    company_id: input.companyId,
    sku: input.sku?.trim() || null,
    name: input.name.trim(),
    description: input.description?.trim() || null,
    category_id: input.categoryId ?? null,
    purchase_unit_id: input.purchaseUnitId,
    stock_unit_id: input.stockUnitId,
    conversion_rate: input.conversionRate ?? 1,
    costing_method: input.costingMethod ?? 'weighted_average',
    reorder_level: input.reorderLevel ?? 0,
    item_type: input.itemType ?? 'resale',
    is_active: input.isActive ?? true,
  };

  try {
    if (input.id) {
      const { data, error } = await supabase
        .from('inventory_items')
        .update(payload)
        .eq('company_id', input.companyId)
        .eq('id', input.id)
        .select('*')
        .single();

      if (error) {
        return { success: false, error: error.message, data: undefined };
      }

      return { success: true, data: mapInventoryItem(data) };
    }

    const { data, error } = await supabase
      .from('inventory_items')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    return { success: true, data: mapInventoryItem(data) };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to save inventory item',
      data: undefined,
    };
  }
};

export const deleteInventoryItem = async (
  companyId: number,
  itemId: string,
): Promise<ApiResponse<null>> => {
  try {
    const { error } = await supabase
      .from('inventory_items')
      .delete()
      .eq('company_id', companyId)
      .eq('id', itemId);

    if (error) {
      return { success: false, error: error.message, data: null };
    }

    return { success: true, data: null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to delete inventory item',
      data: null,
    };
  }
};
